import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Phone, Mail, Bot, Loader2, UserPlus, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { company } from "@/data/site";
import { chatSend, chatLead } from "@/lib/api";
import { cn } from "@/lib/utils";

function sessionId() {
  let id = localStorage.getItem("ntq-chat-session");
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
    localStorage.setItem("ntq-chat-session", id);
  }
  return id;
}

const GREETING = { role: "assistant", content: "Hi, I'm Triq — NeuroTriq TechLabs' AI assistant. Ask me about our services, process or how we can help your business. How can I help today?" };

export default function AssistantWidget() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", message: "" });
  const [leadSent, setLeadSent] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, chatOpen, leadOpen]);

  const openChat = () => {
    setMenuOpen(false);
    setChatOpen(true);
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const res = await chatSend(sessionId(), text);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: `I'm having trouble right now. Reach us at ${company.email} or ${company.phone}.` }]);
    } finally {
      setSending(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (lead.name.trim().length < 2 || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(lead.email)) {
      toast.error("Please enter a valid name and email.");
      return;
    }
    setLeadLoading(true);
    try {
      await chatLead({ ...lead, session_id: sessionId() });
      setLeadSent(true);
      toast.success("Thanks! Our team will reach out shortly.");
      setMessages((m) => [...m, { role: "assistant", content: `Thanks ${lead.name.split(" ")[0]}! I've shared your details with our team — we'll be in touch at ${lead.email} soon.` }]);
      setTimeout(() => { setLeadOpen(false); setLeadSent(false); setLead({ name: "", email: "", message: "" }); }, 1600);
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setLeadLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {/* Chat panel */}
      <AnimatePresence>
        {chatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="flex h-[32rem] w-[calc(100vw-3rem)] max-w-[24rem] flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-2xl"
            data-testid="chat-panel"
          >
            {/* header */}
            <div className="flex items-center justify-between bg-secondary px-4 py-3.5 text-secondary-foreground">
              <div className="flex items-center gap-3">
                <span className="relative grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-primary to-accent text-white">
                  <Bot className="h-5 w-5" />
                  <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-secondary bg-success" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-white">Triq · AI Assistant</p>
                  <p className="text-xs text-white/60">Typically replies instantly</p>
                </div>
              </div>
              <button onClick={() => setChatOpen(false)} data-testid="chat-close" aria-label="Close chat" className="grid h-8 w-8 place-items-center rounded-full text-white/70 hover:bg-white/10 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* messages */}
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-muted/40 p-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                  <div
                    className={cn(
                      "max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
                      m.role === "user" ? "rounded-br-md bg-primary text-primary-foreground" : "rounded-bl-md border border-border bg-card text-foreground"
                    )}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md border border-border bg-card px-4 py-3">
                    {[0, 1, 2].map((d) => (
                      <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: `${d * 0.15}s` }} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* lead form */}
            <AnimatePresence>
              {leadOpen && (
                <motion.form
                  onSubmit={submitLead}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden border-t border-border bg-card p-3"
                  data-testid="chat-lead-form"
                >
                  {leadSent ? (
                    <div className="flex items-center justify-center gap-2 py-3 text-sm text-success"><CheckCircle2 className="h-4 w-4" /> Details sent!</div>
                  ) : (
                    <div className="space-y-2">
                      <input value={lead.name} onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))} placeholder="Your name" data-testid="chat-lead-name" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none" />
                      <input value={lead.email} onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))} placeholder="Your email" type="email" data-testid="chat-lead-email" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none" />
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setLeadOpen(false)} className="flex-1 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted">Cancel</button>
                        <button type="submit" disabled={leadLoading} data-testid="chat-lead-submit" className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60">
                          {leadLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
                        </button>
                      </div>
                    </div>
                  )}
                </motion.form>
              )}
            </AnimatePresence>

            {/* input */}
            <form onSubmit={send} className="flex items-center gap-2 border-t border-border bg-card p-3">
              {!leadOpen && (
                <button type="button" onClick={() => setLeadOpen(true)} data-testid="chat-lead-toggle" title="Leave your details" aria-label="Leave your details" className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary">
                  <UserPlus className="h-4 w-4" />
                </button>
              )}
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about our services..."
                data-testid="chat-input"
                className="min-w-0 flex-1 rounded-full border border-border bg-background px-4 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <button type="submit" disabled={sending || !input.trim()} data-testid="chat-send" aria-label="Send" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick contact menu */}
      <AnimatePresence>
        {menuOpen && !chatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.9 }}
            transition={{ duration: 0.22 }}
            className="flex flex-col items-end gap-2"
          >
            <button onClick={openChat} data-testid="open-ai-chat" className="flex items-center gap-3 rounded-full bg-gradient-to-r from-primary to-accent px-4 py-3 text-sm font-medium text-white shadow-lg transition-transform hover:-translate-y-0.5">
              <Sparkles className="h-4 w-4" /> Chat with Triq (AI)
            </button>
            <a href={`https://wa.me/${company.whatsapp}?text=${encodeURIComponent(company.whatsappMsg)}`} target="_blank" rel="noreferrer" data-testid="whatsapp-button" className="flex items-center gap-3 rounded-full bg-[#25D366] px-4 py-3 text-sm font-medium text-white shadow-lg transition-transform hover:-translate-y-0.5">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <a href={`tel:${company.phoneHref}`} data-testid="floating-call" className="flex items-center gap-3 rounded-full bg-card px-4 py-3 text-sm font-medium text-foreground shadow-lg ring-1 ring-border transition-transform hover:-translate-y-0.5">
              <Phone className="h-4 w-4 text-primary" /> Call us
            </a>
            <a href={`mailto:${company.email}`} data-testid="floating-email" className="flex items-center gap-3 rounded-full bg-card px-4 py-3 text-sm font-medium text-foreground shadow-lg ring-1 ring-border transition-transform hover:-translate-y-0.5">
              <Mail className="h-4 w-4 text-primary" /> Email
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main FAB */}
      {!chatOpen && (
        <button
          onClick={() => setMenuOpen((o) => !o)}
          data-testid="floating-contact-toggle"
          aria-label="Contact and chat options"
          className={cn("grid h-14 w-14 place-items-center rounded-full text-white shadow-xl transition-colors", menuOpen ? "bg-secondary" : "bg-primary")}
        >
          <AnimatePresence mode="wait">
            {menuOpen ? (
              <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
                <X className="h-6 w-6" />
              </motion.span>
            ) : (
              <motion.span key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
                <MessageCircle className="h-6 w-6" />
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      )}
    </div>
  );
}
